import { FileSpreadsheet, LoaderCircle } from 'lucide-react'
import { useState } from 'react'

import { gridToolButtonClass } from '@/components/data-table/grid-styles'
import { Button } from '@/components/ui/button'
import {
  exportRowsToExcel,
  exportTimestamp,
  type ExcelCellValue,
  type ExcelExportColumn,
} from '@/lib/export-excel'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

export interface ExportButtonProps {
  /** Uzantısız temel dosya adı; tarih damgası otomatik eklenir. */
  filename: string
  sheetName?: string
  columns: ExcelExportColumn[]
  rows: ExcelCellValue[][]
  /**
   * Verilirse Excel'e `rows` yerine bunun döndürdüğü satırlar gider (tıklanınca çekilir):
   * sunucu taraflı sayfalanan tabloda ekrandaki sayfa değil bütün kayıtlar.
   */
  getRows?: () => Promise<ExcelCellValue[][]>
  disabled?: boolean
  /** `toolbar`: veri ızgarasının araç çubuğu düğmesi (`DataTable`); varsayılan ikincil düğme. */
  appearance?: 'button' | 'toolbar'
}

/**
 * Tüm tablo ve pivot ekranlarının standart "Excel'e aktar" butonu (görev: tablolarda tek tip export).
 */
export function ExportButton({
  filename,
  sheetName,
  columns,
  rows,
  getRows,
  disabled,
  appearance = 'button',
}: ExportButtonProps) {
  const [pending, setPending] = useState(false)
  const [failed, setFailed] = useState(false)
  const empty = !getRows && rows.length === 0

  const run = async () => {
    setFailed(false)
    setPending(true)
    try {
      exportRowsToExcel({
        filename: `${filename}-${exportTimestamp()}`,
        sheetName,
        columns,
        rows: getRows ? await getRows() : rows,
      })
    } catch (error) {
      console.error('Excel aktarımı başarısız', error)
      setFailed(true)
    } finally {
      setPending(false)
    }
  }

  const title = empty
    ? messages.common.exportEmpty
    : failed
      ? messages.common.exportFailed
      : undefined
  const content = (
    <>
      {pending ? <LoaderCircle className="animate-spin" /> : <FileSpreadsheet />}
      {pending
        ? messages.common.exportPending
        : failed
          ? messages.common.exportFailed
          : messages.common.exportExcel}
    </>
  )

  if (appearance === 'toolbar') {
    return (
      <button
        type="button"
        className={cn(gridToolButtonClass, failed && 'text-danger hover:enabled:text-danger')}
        disabled={disabled || empty || pending}
        title={title}
        aria-busy={pending}
        onClick={() => void run()}
      >
        {content}
      </button>
    )
  }

  return (
    <Button
      variant={failed ? 'destructive' : 'outline'}
      size="sm"
      disabled={disabled || empty || pending}
      title={title}
      aria-busy={pending}
      onClick={() => void run()}
    >
      {content}
    </Button>
  )
}
